/* Showcase Batcher Cockpit — the 5-second cut after the War Room peep on the public
   vault entrance. A replica of the real cockpit: the aggregate weights climb live, the
   stage steps LOADING AGG → LOAD TRUCK → DISCHARGING → LOAD COMPLETE. All simulated
   client-side; no auth, no API, no real plant telemetry. */
import { useEffect, useRef, useState } from "react";
import { vol, mass, liq, slump, mixLabel, IMP } from "@/lib/showcaseUnits";
import { SITES } from "@/lib/showcaseCity";

const ORANGE = "#F57C00";
const HERO_IMG = "/images/plant-hero-clean.jpg";

const MATS = [
  { key: "agg_20mm", bin: 1, label: "20 MM AGG", target: 700, unit: "kg", colour: "#075985" },
  { key: "agg_14mm", bin: 2, label: "14 MM AGG", target: 300, unit: "kg", colour: "#0369A1" },
  { key: "agg_10mm", bin: 3, label: "10 MM AGG", target: 180, unit: "kg", colour: "#0284C7" },
  { key: "agg_7mm", bin: 4, label: "7 MM AGG", target: 120, unit: "kg", colour: "#0EA5E9" },
  { key: "man_dust", bin: 5, label: "MAN DUST", target: 260, unit: "kg", colour: "#A16207" },
  { key: "sand", bin: 6, label: "SAND", target: 620, unit: "kg", colour: "#EAB308" },
  { key: "cement", bin: null, label: "CEMENT (GP)", target: 320, unit: "kg", colour: "#A1A1AA" },
  { key: "water", bin: null, label: "WATER", target: 168, unit: "L", colour: "#38BDF8" },
];
const conv = (row, value) => (row.unit === "L" ? liq(value) : mass(value));
const AGG_TOTAL = MATS.filter((m) => m.bin).reduce((s, m) => s + m.target, 0);
const WEIGH_MS = 3200;   // all materials made weight by here
const FLAG_MS = 3500;    // LOAD TRUCK
const DISCHARGE_MS = 4100;
const DONE_MS = 4700;

export default function ShowcaseBatcher() {
  const [ms, setMs] = useState(0);
  const start = useRef(Date.now());
  useEffect(() => {
    const i = setInterval(() => setMs(Date.now() - start.current), 80);
    return () => clearInterval(i);
  }, []);

  const p = Math.min(1, ms / WEIGH_MS);
  const loadedKg = AGG_TOTAL * p;                       // cumulative agg through the weigher
  const done = ms >= DONE_MS;
  const discharging = ms >= DISCHARGE_MS && !done;
  const flagged = ms >= FLAG_MS && !discharging && !done;
  const binKg = done ? 6 : discharging ? AGG_TOTAL * (1 - (ms - DISCHARGE_MS) / (DONE_MS - DISCHARGE_MS)) : loadedKg;

  const stage = done ? 5 : discharging ? 4 : flagged ? 3 : 2;
  const stageLabel = ["PREP · MANUAL", "LOADING AGG", "LOAD TRUCK", "DISCHARGING", "COMPLETE"][stage - 1];

  // Walk the cumulative agg weight down the material list: anything fully passed is
  // locked, the one in the window is live, the rest are still to come.
  let seen = 0;
  const rows = MATS.map((m) => {
    if (!m.bin) {
      // cement + water are keyed in by hand at this plant — they land early
      const captured = ms > 900;
      return { ...m, actual: captured ? m.target * (m.key === "water" ? 1.006 : 0.997) : null, live: false };
    }
    const from = seen; seen += m.target;
    const filled = Math.min(m.target, Math.max(0, loadedKg - from));
    const locked = loadedKg >= seen - 1;
    return { ...m, actual: locked ? m.target * (1 + ((m.target % 7) - 3) / 400) : filled > 2 ? filled : null, live: !locked && filled > 2 };
  });
  const weighed = rows.filter((r) => r.actual != null && !r.live).length;

  return (
    <div className="absolute inset-0 flex flex-col bg-black text-zinc-200 overflow-hidden select-none" data-testid="showcase-batcher">
      {/* ── cockpit header bar ─────────────────────────────────────── */}
      <div className="border-b border-zinc-800 bg-[#0E1013] flex flex-wrap items-stretch divide-x divide-zinc-800 pr-28">
        <div className="px-4 py-2">
          <div className="mono text-[9px] uppercase tracking-[0.3em] text-zinc-500">Mix · Load</div>
          <div className="flex items-baseline gap-2">
            <div className="heading text-3xl font-black !text-white leading-none">{mixLabel("N32-20", 32, 20)}</div>
            <div className="mono text-[11px] text-zinc-300">{vol(7.5).v.toFixed(1)} {vol(7.5).u} · {slump(80).v} {slump(80).u} slump</div>
          </div>
          <div className="mono text-[10px] text-zinc-500 mt-0.5">load 2 of 3 · {IMP ? '3/4" · ASTM C94' : "20 mm · AS 1379"}</div>
        </div>
        <div className="px-4 py-2 min-w-0">
          <div className="mono text-[9px] uppercase tracking-[0.3em] text-zinc-500">Truck · Customer</div>
          <div className="flex items-baseline gap-2 min-w-0">
            <div className="heading text-3xl font-black !text-white leading-none">SP-11</div>
            <div className="mono text-[11px] text-zinc-300 truncate">Hargraves Civil</div>
          </div>
          <div className="mono text-[10px] text-zinc-500 truncate">J. Petrie · Lot 22 Ocean Pde, {SITES[1].name}</div>
        </div>
        <div className="px-4 py-2 min-w-[190px]">
          <div className="mono text-[9px] uppercase tracking-[0.3em] text-zinc-500">Stage</div>
          <div className={`heading text-lg font-black leading-none whitespace-nowrap ${stage === 5 ? "!text-emerald-400" : stage === 3 ? "!text-emerald-300 animate-pulse" : "!text-white"}`}>
            {stage} · {stageLabel}
          </div>
          <div className="h-1.5 bg-zinc-800 mt-1.5 relative">
            <div className="absolute inset-y-0 left-0 transition-[width] duration-200"
              style={{ width: `${Math.min(100, (Math.max(binKg, loadedKg) / AGG_TOTAL) * 100)}%`, background: stage >= 3 ? "#22C55E" : ORANGE }} />
          </div>
          <div className="mono text-[10px] text-zinc-400 mt-0.5 tabular-nums">
            agg bin {mass(binKg).v.toLocaleString()} / {mass(AGG_TOTAL).v.toLocaleString()} {mass(0).u}
          </div>
        </div>
        <div className="px-4 py-2">
          <div className="mono text-[9px] uppercase tracking-[0.3em] text-zinc-500">Status</div>
          <div className="min-h-[30px] flex items-center">
            {done ? <div className="heading text-2xl font-black !text-emerald-400 leading-none" data-testid="showcase-load-complete">✓ LOAD COMPLETE</div>
              : discharging ? <div className="heading text-2xl font-black leading-none" style={{ color: ORANGE }}>DISCHARGING</div>
                : flagged ? <div className="heading text-2xl font-black !text-emerald-400 leading-none animate-pulse">▶ LOAD TRUCK</div>
                  : <div className="heading text-2xl font-black leading-none" style={{ color: ORANGE }}>BATCHING</div>}
          </div>
          <div className="mono text-[9px] uppercase tracking-widest mt-1 flex gap-2">
            <span style={{ color: ORANGE }}>● LIVE</span>
            <span className="text-emerald-400">▲ RINSTRUM LIVE</span>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-row flex-nowrap">
        {/* ── LEFT · plant + gauges ───────────────────────────────── */}
        <div className="relative basis-[42%] w-[42%] shrink-0 border-r border-zinc-800 overflow-hidden hidden md:block">
          <img src={HERO_IMG} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-90" draggable={false} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,.35), rgba(0,0,0,.82))" }} />
          <div className="absolute top-3 left-3 mono text-[9px] uppercase tracking-[0.3em] text-zinc-400">Plant P01 · Silos & Bins</div>
          <div className="absolute inset-x-3 bottom-3 grid grid-cols-4 gap-2">
            {[
              { l: "SILO A", v: 42.6, c: 60, u: "t" },
              { l: "SILO B", v: 18.2, c: 60, u: "t" },
              { l: "20 MM", v: 58.4, c: 80, u: "t" },
              { l: "SAND", v: 26.9, c: 60, u: "t" },
            ].map((s) => {
              const pct = Math.round((s.v / s.c) * 100);
              const low = pct < 35;
              return (
                <div key={s.l} className="border border-zinc-700 bg-black/70 backdrop-blur px-2 py-1.5">
                  <div className="mono text-[8px] tracking-[0.2em] text-zinc-500 uppercase">{s.l}</div>
                  <div className="mono text-sm font-black tabular-nums" style={{ color: low ? "#F59E0B" : "#E4E4E7" }}>
                    {(IMP ? s.v * 1.10231 : s.v).toFixed(1)}<span className="text-[9px] text-zinc-500 ml-0.5">{IMP ? "tn" : s.u}</span>
                  </div>
                  <div className="h-1 bg-zinc-800 mt-1">
                    <div className="h-full" style={{ width: `${pct}%`, background: low ? "#F59E0B" : ORANGE }} />
                  </div>
                </div>
              );
            })}
          </div>
          {/* live agg weigher readout, as on the cockpit */}
          <div className="absolute top-10 left-3 border border-zinc-700 bg-black/75 backdrop-blur px-3 py-2">
            <div className="mono text-[8px] tracking-[0.25em] text-zinc-500 uppercase">Agg weigher · live</div>
            <div className="mono text-3xl font-black tabular-nums" style={{ color: stage >= 3 ? "#22C55E" : ORANGE }}>
              {mass(binKg).v.toLocaleString()}<span className="text-sm text-zinc-500 ml-1">{mass(0).u}</span>
            </div>
            <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">
              {done ? "bin empty · docket issued" : discharging ? "discharging to truck" : `weighing ${weighed}/${MATS.length}`}
            </div>
          </div>
        </div>

        {/* ── RIGHT · weights table ───────────────────────────────── */}
        <div className="flex-1 min-w-0 flex flex-col bg-[#0B0C0E]">
          <div className="px-3 py-2 border-b border-zinc-800 flex items-center gap-2">
            <div className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Live weights · tolerance ±1%</div>
            <span className="ml-auto mono text-[9px] uppercase tracking-widest text-emerald-400">streaming · rinstrum r420</span>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            <table className="w-full mono text-[11px]">
              <thead>
                <tr className="text-[8px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                  <th className="text-left px-2 py-1">Material</th>
                  <th className="text-right px-2 py-1">Target</th>
                  <th className="text-right px-2 py-1">To go</th>
                  <th className="text-right px-2 py-1">Actual<span className="text-emerald-400"> · live</span></th>
                  <th className="px-2 py-1 w-[22%]">Tolerance</th>
                  <th className="text-right px-2 py-1">Δ %</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const shown = r.actual;
                  const d = shown != null && r.target ? ((shown - r.target) / r.target) * 100 : 0;
                  const pct = shown != null ? Math.min(120, (shown / r.target) * 100) : 0;
                  const locked = shown != null && !r.live;
                  const toGo = shown != null ? Math.max(0, r.target - shown) : r.target;
                  return (
                    <tr key={r.key} className={`border-b border-zinc-800/60 h-[30px] ${r.live ? "bg-amber-500/10" : ""}`}>
                      <td className="px-2 py-0.5 uppercase tracking-wide font-black text-[10px]" style={{ color: r.colour }}>
                        {r.bin ? <span className="mono text-[8px] text-zinc-500 mr-1">BIN {r.bin}</span> : null}{r.label}
                      </td>
                      <td className="px-2 py-0.5 text-right text-zinc-300 tabular-nums">
                        {conv(r, r.target).v.toLocaleString()} <span className="text-zinc-600 text-[9px]">{conv(r, r.target).u}</span>
                      </td>
                      <td className={`px-2 py-0.5 text-right tabular-nums font-black ${locked ? "text-zinc-600" : toGo <= 0 ? "text-emerald-400" : ""}`}
                        style={!locked && toGo > 0 ? { color: ORANGE } : undefined}>
                        {locked ? "—" : conv(r, toGo).v.toLocaleString()}
                      </td>
                      <td className="px-2 py-0.5 text-right tabular-nums font-black text-zinc-100">
                        {shown == null ? <span className="text-zinc-700">—</span> : conv(r, shown).v.toLocaleString()}
                      </td>
                      <td className="px-2 py-0.5">
                        <div className="h-1.5 bg-zinc-800 relative">
                          <div className="h-full transition-[width] duration-150"
                            style={{ width: `${pct}%`, background: locked ? "#22C55E" : ORANGE }} />
                          <div className="absolute inset-y-[-2px] w-px bg-zinc-600" style={{ left: "97%" }} />
                          <div className="absolute inset-y-[-2px] w-px bg-zinc-600" style={{ left: "103%" }} />
                        </div>
                      </td>
                      <td className={`px-2 py-0.5 text-right tabular-nums ${shown == null ? "text-zinc-700" : Math.abs(d) > 3 ? "text-red-400" : "text-emerald-400"}`}>
                        {shown == null ? "—" : `${d >= 0 ? "+" : ""}${d.toFixed(1)}%`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="border-t border-zinc-800 px-3 py-2 flex items-center gap-3">
            <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">W/C 0.52 · design 0.55 max</div>
            <div className={`ml-auto mono text-[10px] uppercase tracking-widest font-black px-3 py-1.5 border ${
              done ? "border-emerald-500 text-emerald-400" : flagged ? "border-emerald-400 text-black animate-pulse" : "border-zinc-700 text-zinc-500"}`}
              style={flagged ? { background: "#22C55E" } : undefined}>
              {done ? "✓ Docket D260926-0151 issued" : flagged ? "▶ Load truck" : "Load truck"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
